import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HeartHandshake, Gift } from "lucide-react";
import { showSuccess, showError } from "@/utils/toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

interface ThankYouEntry {
  id: string;
  user: string;
  message: string;
  date: string;
  avatar_url?: string;
  translater?: boolean;
  admin?: boolean;
}

interface GameThanksTabProps {
  gameId: number;
}

const truncateName = (name: string, maxLength: number = 8): string => {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength) + '...';
};

const GameThanksTab: React.FC<GameThanksTabProps> = ({ gameId }) => {
  const { isLoggedIn, currentUser } = useAuth();
  const [thankYous, setThankYous] = useState<ThankYouEntry[]>([]);
  const [hasThanked, setHasThanked] = useState(false);
  const [loading, setLoading] = useState(true);
  const token = document.querySelector('meta[name="csrf-token"]').content;

  const userHasValidName = React.useMemo(() => {
    if (!currentUser?.name) return false;
    // Ellenőrizzük, hogy a név nem csak számokból áll-e (az alapértelmezett, generált név)
    return !/^\d+$/.test(currentUser.name);
  }, [currentUser]);

  useEffect(() => {
    const fetchThanks = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/thanks?game_id=${gameId}`);
        if (!res.ok) throw new Error('Nem sikerült lekérni a köszöneteket');

        const data = await res.json();

        const apiThanks: ThankYouEntry[] = data.users.map((u: any) => ({
          id: `api-${u.id}`,
          user: u.name,
          message: 'Köszönöm a munkátokat!',
          date: '',
          avatar_url: u.avatar_url || undefined,
          translater: u.translater,
          admin: u.admin
        }));

        setThankYous(apiThanks);
        setHasThanked(data.already_thanked || false); // Ha nincs bejelentkezve, akkor false
      } catch (error: any) {
        showError(error.message || 'Hiba a köszönetek lekérésekor');
        setThankYous([]);
        setHasThanked(false);
      } finally {
        setLoading(false);
      }
    };

    fetchThanks();
  }, [gameId]);

  const handleThankYou = async () => {


    if (!isLoggedIn) {
      showError('A köszönöm üzenet küldéséhez be kell jelentkeznie!');
      return;
    }
    if (!userHasValidName) {
      showError('A köszönöm üzenet küldéséhez be kell állítanod egy nevet a profilodban!');
      return;
    }

    if (hasThanked) {
      showError('Már megköszönted ezt a fordítást!');
      return;
    }

    try {
      const res = await fetch('/api/thanks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({ game_id: gameId })

      });

      if (res.status === 201) {
        const newThankYou: ThankYouEntry = {
          id: `t${Date.now()}`,
          user: currentUser?.translaterName || currentUser?.alias || currentUser?.name || 'Névtelen',
          message: 'Köszönöm a munkátokat!',
          date: new Date().toLocaleString('hu-HU'),
          avatar_url: currentUser?.avatar_url || undefined,
        };
        setThankYous([newThankYou, ...thankYous]);
        setHasThanked(true);
        showSuccess('Köszönöm üzenet elküldve!');
      } else {
        const errorData = await res.json();
        showError(errorData.error || 'Nem sikerült elküldeni a köszönetet.');
      }
    } catch (e: any) {
      showError(e.message || 'Hiba történt a köszönet elküldésekor.');
    }
  };

  return (
    <Card className="bg-gray-800 text-secondary-foreground border-border shadow-md">
      <CardHeader>
        <div className="flex items-center">
          <HeartHandshake size={20} className="mr-2 text-primary" />
          <CardTitle className="text-xl font-bold text-primary">Köszönöm</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Köszönöm a fordítást!</h3>
          <p className="text-gray-400 max-w-2xl mx-auto">A fordítóink rengeteg időt és energiát fektetnek abba, hogy kedvenc játékaidat magyar nyelven élvezhesd. Néha egy köszönöm sokat jelent nekik a munka folytatásában!</p>
        </div>

        {!isLoggedIn && (
          <p className="text-center text-red-400 font-semibold mb-4">
            A köszönetnyilvánításhoz be kell jelentkezned!
          </p>
        )}

        {isLoggedIn && !userHasValidName && (
          <p className="text-center bg-destructive/20 text-destructive-foreground p-3 rounded-md mb-4 text-sm">
            A köszönetnyilvánításhoz be kell állítanod egy nevet a profilodban.
            <Button asChild variant="link" className="font-bold underline text-blue-400 hover:text-blue-300 p-1 h-auto">
              <Link to="/settings">
                Kattints ide a beállításhoz!
              </Link>
            </Button>
          </p>
        )}

        <Button
          onClick={handleThankYou}
          disabled={hasThanked || !isLoggedIn || !userHasValidName || loading}
          className="w-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <HeartHandshake size={16} className="mr-2" />
          {hasThanked ? 'Már megköszönted!' : loading ? 'Betöltés...' : 'Köszönöm a fordítást!'}
        </Button>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold mb-3 text-foreground">Akik már megköszönték:</h3>
          {thankYous.length === 0 ? (
            <p className="text-muted-foreground text-center">Még senki sem köszönte meg. Légy te az első!</p>
          ) : (
            <TooltipProvider>
              <div className="flex flex-wrap gap-3 justify-center">
                {thankYous.map((ty) => (
                  <>
                    {ty.user != 'Névtelen' && (
                      <div key={ty.id} className="flex flex-col items-center text-center text-sm text-muted-foreground">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Avatar className="w-12 h-12 mb-1 cursor-help">
                              {ty.avatar_url ? (
                                <AvatarImage src={ty.avatar_url} alt={ty.user} className="object-cover" />
                              ) : (
                                <AvatarFallback className="bg-primary text-primary-foreground text-lg">{ty.user.charAt(0)}</AvatarFallback>
                              )}
                            </Avatar>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{ty.user}</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className={`font-semibold text-xs cursor-help ${ty.admin
                                ? "text-red-500"
                                : ty.translater
                                  ? "text-green-500"
                                  : "text-foreground"
                                }`}
                            >
                              {truncateName(ty.user, 8)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{ty.user}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </>
                ))}
              </div>
            </TooltipProvider>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GameThanksTab;
